import { useMemo, useState } from "react";
import { useTable } from "react-table";
import primaryComponents from "../../../../primaryComponents";
import styles from "./styles.module.scss";
import { DeleteIcon, PlusCircle } from "../../../../../assets/svgIcons";
import { useSelector } from "react-redux";
import {
  useGetExpenseCategories,
  useGetExpenseDepartments,
} from "../../../../../hooks/api/queries/useExpenses";

export const useProducts = ({
  bulkForm,
  setBulkForm = () => {},
  ...validationParams
}) => {
  const [isOtherDetails, setIsOtherDetails] = useState(null);
  const {
    control,
    register,
    trigger,
    formState: { errors },
    setValue,
  } = validationParams;
  const { storeList } = useSelector((state) => state.profileDetailsReducer);
  const { data: expenseCategories = [] } = useGetExpenseCategories();
  const { data } = useGetExpenseDepartments({});
  const expenseDepartments = data?.data;
  const paymentMethods = [
    { name: "Transfer", id: "transfer" },
    { name: "Card", id: "card" },
    { name: "Cash", id: "cash" },
  ];

  const [views, setViews] = useState({
    isCategory: false,
    isDepartment: false,
  });

  const columns = useMemo(
    () => [
      {
        Header: (
          <>
            <span className="error--text">*</span>Expense Name
          </>
        ),
        accessor: "name",
        Cell: ({ cell: { value }, row }) => (
          <span className="d-flex align-items-center">
            <span
              className="mr-2 cursor-pointer"
              onClick={() => deleteRow(row?.index)}
            >
              <DeleteIcon />
            </span>

            <primaryComponents.InputFieldV2
              control={control}
              name={`expenses.${row?.index}.name`}
              register={register}
              placeholder={"Enter name"}
              classNames={`white ${styles.inputField2}`}
              value={value}
              errorMessage={errors?.expenses?.[row?.index]?.name}
              floatError={false}
              onKeyUp={() =>
                errors?.expenses?.[row?.index]?.name &&
                trigger(`expenses.${row?.index}.name`)
              }
              onChange={({ target: { value } }) => {
                const updatedItems = [...instance?.data];
                updatedItems[row.index].name = value;

                setBulkForm((prevState) => ({
                  ...prevState,
                  expenses: updatedItems,
                }));
              }}
            />
          </span>
        ),
      },
      {
        Header: (
          <>
            <span className="error--text">*</span>Amount{" "}
            <span className="text--purple small-text-size">(VAT incl.)</span>
          </>
        ),
        accessor: "amount",
        Cell: ({ cell: { value }, row }) => (
          <primaryComponents.AmountFieldV2
            control={control}
            name={`expenses.${row?.index}.amount`}
            register={register}
            placeholder={"Enter Amount"}
            classNames={`white ${styles.inputField}`}
            value={value}
            currency_code={
              storeList?.find(
                (store) => `${store?.id}` === `${bulkForm.store_id}`,
              )?.currency_code
            }
            errorMessage={errors?.expenses?.[row?.index]?.amount}
            floatError={false}
            onKeyUp={() =>
              errors?.expenses?.[row?.index]?.amount &&
              trigger(`expenses.${row?.index}.amount`)
            }
            onChange={({ target: { value } }) => {
              const updatedItems = [...instance?.data];
              updatedItems[row.index].amount = value;

              setBulkForm((prevState) => ({
                ...prevState,
                expenses: updatedItems,
              }));
            }}
          />
        ),
      },
      {
        Header: <>VAT rate</>,
        accessor: "vat_rate",
        Cell: ({ cell: { value }, row }) => (
          <primaryComponents.VatPercentField
            name={`expenses.${row?.index}.vat_rate`}
            control={control}
            classNames={`white ${styles.tableVatPercentField}`}
            value={instance?.data[row?.index]?.vat_rate}
            onChange={({ percentValue }) => {
              const updatedItems = [...instance?.data];
              updatedItems[row.index].vat_rate =
                percentValue === "" ? 0 : percentValue;

              setBulkForm((prevState) => ({
                ...prevState,
                expenses: updatedItems,
              }));
            }}
            onKeyUp={() =>
              errors?.expenses?.[row?.index]?.vat_rate &&
              trigger(`expenses.${row?.index}.vat_rate`)
            }
            type="number"
            min="0"
            max="100"
            step="0.01"
            errorMessage={errors?.expenses?.[row?.index]?.vat_rate}
            dividedValue={instance?.data[row?.index]?.amount || 0}
          />
        ),
      },
      {
        Header: (
          <>
            <span className="error--text">*</span>Expense Date
          </>
        ),
        accessor: "expense_date",
        Cell: ({ cell: { value }, row }) => (
          <primaryComponents.DateInputV2
            control={control}
            name={`expenses.${row?.index}.expense_date`}
            register={register}
            classNames={`white ${styles.inputField}`}
            errorMessage={errors?.expenses?.[row?.index]?.expense_date}
            floatError={false}
            value={value}
            isRequired
            onKeyUp={() =>
              errors?.expenses?.[row?.index]?.expense_date &&
              trigger(`expenses.${row?.index}.expense_date`)
            }
            onChange={({ target: { value } }) => {
              const updatedItems = [...instance?.data];
              updatedItems[row.index].expense_date = value;

              setBulkForm((prevState) => ({
                ...prevState,
                expenses: updatedItems,
              }));
            }}
          />
        ),
      },
      {
        Header: (
          <>
            <span className="error--text">*</span>Category (
            <span
              className="purple--text cursor-pointer"
              onClick={() =>
                setViews((prev) => ({ ...prev, isCategory: true }))
              }
            >
              Add Category
            </span>
            )
          </>
        ),
        accessor: "category",
        Cell: ({ row, cell: { value } }) => (
          <primaryComponents.SelectFieldV2
            control={control}
            name={`expenses.${row?.index}.category`}
            register={register}
            classNames={`white ${styles.inputField}`}
            errorMessage={errors?.expenses?.[row?.index]?.category}
            floatError={false}
            value={
              expenseCategories?.find(
                (category) => `${category?.id}` === `${value}`,
              )?.name || "Select"
            }
            options={expenseCategories}
            nameKey={"name"}
            idKey={"id"}
            onChange={(value) => {
              const updatedItems = [...instance?.data];
              updatedItems[row.index].category = value;

              setBulkForm((prevState) => ({
                ...prevState,
                expenses: updatedItems,
              }));
            }}
          />
        ),
      },
      {
        Header: (
          <>
            <span className="error--text">*</span>Payment Method
          </>
        ),
        accessor: "payment_method",
        Cell: ({ row, cell: { value } }) => (
          <primaryComponents.SelectFieldV2
            control={control}
            name={`expenses.${row?.index}.payment_method`}
            register={register}
            classNames={`white ${styles.inputField}`}
            errorMessage={errors?.expenses?.[row?.index]?.payment_method}
            floatError={false}
            isRequired
            options={paymentMethods}
            value={
              paymentMethods?.find(
                (paymentMethod) => `${paymentMethod?.id}` === `${value}`,
              )?.name || "Select"
            }
            nameKey={"name"}
            idKey={"id"}
            onChange={(value) => {
              const updatedItems = [...instance?.data];
              updatedItems[row.index].payment_method = value;

              setBulkForm((prevState) => ({
                ...prevState,
                expenses: updatedItems,
              }));
            }}
          />
        ),
      },
      {
        Header: (
          <>
            Department <br />(
            <span
              className="purple--text cursor-pointer"
              onClick={() =>
                setViews((prev) => ({ ...prev, isDepartment: true }))
              }
            >
              Add Department
            </span>
            )
          </>
        ),
        accessor: "department",
        Cell: ({ row, cell: { value } }) => (
          <primaryComponents.SelectFieldV2
            control={control}
            name={`expenses.${row?.index}.department`}
            register={register}
            classNames={`white ${styles.inputField}`}
            errorMessage={errors?.expenses?.[row?.index]?.department}
            options={expenseDepartments}
            nameKey={"name"}
            idKey={"id"}
            floatError={false}
            value={
              expenseDepartments?.find(
                (department) => `${department?.id}` === `${value}`,
              )?.name || "Select"
            }
            onChange={(value) => {
              const updatedItems = [...instance?.data];
              updatedItems[row.index].department = value;

              setBulkForm((prevState) => ({
                ...prevState,
                expenses: updatedItems,
              }));
            }}
          />
        ),
      },
      {
        Header: "Other Details",
        Cell: ({ row }) => (
          <span
            className={" d-flex align-items-center cursor-pointer purple--text"}
            onClick={() => {
              setIsOtherDetails({
                index: row.index,
                id: row?.original?.id,
              });
            }}
          >
            <PlusCircle className="mr-1" />
            <span>Add</span>
          </span>
        ),
      },
    ],
    [
      errors,
      bulkForm?.store_id,
      bulkForm?.expenses?.length,
      expenseCategories,
      expenseDepartments,
    ],
  );

  const instance = useTable({
    columns,
    data: bulkForm?.expenses || [],
  });

  const deleteRow = (index) => {
    const updatedItems = [...instance?.data];
    updatedItems.splice(index, 1);

    setBulkForm((prevState) => ({
      ...prevState,
      expenses: updatedItems,
    }));

    setValue("expenses", updatedItems);
  };

  return {
    instance,
    isOtherDetails,
    views,
    setViews,
    setIsOtherDetails,
  };
};
